/**
 * Profile utility functions
 * Reusable functions for profile-related operations
 */

import { DATE_FORMAT_OPTIONS, SKILL_LEVELS } from "../constants/profile";

/**
 * Formats a date string to Korean locale format
 * @param dateString - ISO date string
 * @returns Formatted date string in Korean
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("ko-KR", DATE_FORMAT_OPTIONS);
};

/**
 * Gets the appropriate color class for skill level
 * @param level - Skill level string
 * @returns Tailwind CSS color class
 */
export const getSkillLevelColor = (level: keyof typeof SKILL_LEVELS): string => {
  return SKILL_LEVELS[level] || SKILL_LEVELS.초급;
};

/**
 * Formats file size in bytes to human readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

/**
 * Calculates the progress percentage for certification validity
 * @param issueDate - Certification issue date
 * @param expiryDate - Certification expiry date
 * @returns Progress percentage (0-100)
 */
export const calculateCertificationProgress = (
  issueDate: string,
  expiryDate: string
): number => {
  const now = new Date().getTime();
  const issue = new Date(issueDate).getTime();
  const expiry = new Date(expiryDate).getTime();
  
  const totalDuration = expiry - issue;
  const remainingTime = expiry - now;
  
  const progress = Math.max(0, Math.min(100, (remainingTime / totalDuration) * 100));
  return Math.round(progress);
};

/**
 * Validates email format
 * @param email - Email string to validate
 * @returns Boolean indicating if email is valid
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return emailRegex.test(email);
};

/**
 * Validates URL format
 * @param url - URL string to validate
 * @returns Boolean indicating if URL is valid
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Truncates text to specified length with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
};

/**
 * Generates a unique ID for components
 * @param prefix - Optional prefix for the ID
 * @returns Unique ID string
 */
export const generateId = (prefix: string = "id"): string => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};
